import { Link, NavLink, Outlet } from 'react-router-dom';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { BRAND } from '@fin/shared/brand';
import { CONTENT_GROUPS, STAFF_GROUPS, type CognitoGroup } from '@fin/shared/enums';
import { homePathFor, useAuth } from '../lib/auth';
import { NotificationsBell } from './NotificationsBell';

// `groups` = who sees the tab (must match the route gates in App.tsx).
const tabs: Array<{ to: string; label: string; end?: boolean; groups: readonly CognitoGroup[] }> = [
  { to: '/', label: 'Dashboard', end: true, groups: STAFF_GROUPS },
  { to: '/today', label: 'Today', groups: STAFF_GROUPS },
  { to: '/workers', label: 'Workers', groups: STAFF_GROUPS },
  { to: '/customers', label: 'Customers', groups: STAFF_GROUPS },
  { to: '/jobs', label: 'Jobs', groups: STAFF_GROUPS },
  { to: '/invoices', label: 'Invoices', groups: STAFF_GROUPS },
  { to: '/calendar', label: 'Calendar', groups: STAFF_GROUPS },
  { to: '/feedback', label: 'Feedback', groups: STAFF_GROUPS },
  { to: '/content', label: 'Content', groups: CONTENT_GROUPS },
  { to: '/how-it-works', label: 'Help', groups: STAFF_GROUPS },
  { to: '/settings', label: 'Settings', groups: STAFF_GROUPS },
];

export function AppShell() {
  const { user, signOut } = useAuth();
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: BRAND.surfaceMuted }}>
      <header
        className="sticky top-0 z-20 shadow-lg"
        style={{
          backgroundImage: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primarySoft} 100%)`,
        }}
      >
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-2 gap-y-2 px-3 py-2.5 sm:gap-x-3 sm:px-6 sm:py-3">
          <Link to={homePathFor(user?.groups)} className="flex min-w-0 items-center gap-2 group sm:gap-3">
            <img
              src="/logo.png"
              alt="FixItNinjaz"
              className="h-7 w-auto shrink-0 transition-transform group-hover:scale-105 sm:h-8"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
            <span
              className="hidden text-xs font-medium uppercase tracking-[0.2em] sm:inline"
              style={{ color: BRAND.accent }}
            >
              Admin
            </span>
          </Link>
          <div className="flex shrink-0 items-center gap-1.5 text-xs text-white/80 sm:gap-2">
            <RefreshButton />
            <NotificationsBell />
            <span className="hidden max-w-[16ch] truncate md:inline">{user?.email}</span>
            <button
              onClick={() => void signOut()}
              title="Sign out of the admin portal"
              className="rounded-md border border-white/20 bg-white/5 px-2 py-1 text-white backdrop-blur transition hover:bg-white/10 sm:px-2.5"
            >
              Sign out
            </button>
          </div>
          <nav className="-mx-3 flex w-full gap-0.5 overflow-x-auto px-3 pb-1 text-base sm:mx-0 sm:w-auto sm:overflow-visible sm:px-0 sm:pb-0 sm:text-sm">
            {tabs
              .filter((t) => t.groups.some((g) => user?.groups?.includes(g)))
              .map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-lg px-3 py-2 font-medium transition sm:py-1.5 ${
                    isActive
                      ? 'bg-white text-stone-900 shadow-sm'
                      : 'text-white/85 hover:bg-white/10 hover:text-white'
                  }`
                }
              >
                {t.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>
      <main className="mx-auto w-full max-w-6xl flex-1 px-3 py-4 sm:px-6 sm:py-6">
        <Outlet />
      </main>
      <footer className="border-t border-stone-200 bg-white/50 px-3 py-3 text-center text-xs text-stone-500">
        © {new Date().getFullYear()} FixItNinjaz · Trusted handyman & maintenance
      </footer>
    </div>
  );
}

function RefreshButton() {
  const qc = useQueryClient();
  const fetching = useIsFetching();
  const spinning = fetching > 0;
  return (
    <button
      type="button"
      onClick={() => void qc.invalidateQueries()}
      disabled={spinning}
      title="Refresh data"
      aria-label="Refresh"
      className="rounded-md border border-white/20 bg-white/5 p-1.5 text-white backdrop-blur transition hover:bg-white/10 disabled:opacity-60"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={spinning ? 'animate-spin' : undefined}
      >
        <path d="M21 12a9 9 0 1 1-3-6.7" />
        <path d="M21 4v5h-5" />
      </svg>
    </button>
  );
}
